import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useToast } from '@/lib/hooks/use-toast'
import { integrationService, type SharePointConfig } from '@/lib/services/integrationService'
import SharePointLogo from '@/assets/sharepoint.svg'
import { FileText } from 'lucide-react'

const sharePointSchema = z.object({
  spTenantId: z.string().min(1, 'Tenant ID is required'),
  spClientId: z.string().min(1, 'Client ID is required'),
  spCertThumbprint: z.string().min(1, 'Certificate thumbprint is required'),
  spHost: z.string().min(1, 'SharePoint host is required'),
  spSitePath: z.string().min(1, 'Site path is required'),
  spPiketnummersPath: z.string().optional(),
  spHulpdienstenPath: z.string().optional(),
  spBetrokkenPartijenPath: z.string().optional(),
  spCrisiscommunicatiePath: z.string().optional(),
})

type SharePointFormData = z.infer<typeof sharePointSchema>

interface SharePointConfigDialogProps {
  open: boolean
  onClose: () => void
  onSuccess: () => void
}

export function SharePointConfigDialog({ open, onClose, onSuccess }: SharePointConfigDialogProps) {
  const { t } = useTranslation()
  const { toast } = useToast()
  const [privateKeyFile, setPrivateKeyFile] = useState<File | null>(null)
  const [hasExistingKey, setHasExistingKey] = useState(false)

  // Fetch existing configuration
  const { data: existingConfig } = useQuery({
    queryKey: ['integration', 'sharepoint', 'config'],
    queryFn: () => integrationService.getIntegration('sharepoint', true),
    enabled: open,
    retry: false,
    throwOnError: false,
  })

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<SharePointFormData>({
    resolver: zodResolver(sharePointSchema),
    defaultValues: {
      spTenantId: '',
      spClientId: '',
      spCertThumbprint: '',
      spHost: '',
      spSitePath: '',
    },
  })

  // Update form when existingConfig arrives
  useEffect(() => {
    if (existingConfig?.config) {
      const config = existingConfig.config as Record<string, unknown>

      // Check if a private key already exists
      const hasKey = !!(config.spPrivateKeyPem || config.sp_private_key_pem)
      setHasExistingKey(hasKey)

      // Try both camelCase and snake_case keys since the API might return either
      reset({
        spTenantId: (config.spTenantId || config.sp_tenant_id || '') as string,
        spClientId: (config.spClientId || config.sp_client_id || '') as string,
        spCertThumbprint: (config.spCertThumbprint || config.sp_cert_thumbprint || '') as string,
        spHost: (config.spHost || config.sp_host || '') as string,
        spSitePath: (config.spSitePath || config.sp_site_path || '') as string,
        spPiketnummersPath: (config.spPiketnummersPath || config.sp_piketnummers_path || '') as string,
        spHulpdienstenPath: (config.spHulpdienstenPath || config.sp_hulpdiensten_path || '') as string,
        spBetrokkenPartijenPath: (config.spBetrokkenPartijenPath || config.sp_betrokken_partijen_path || '') as string,
        spCrisiscommunicatiePath: (config.spCrisiscommunicatiePath || config.sp_crisiscommunicatie_path || '') as string,
      })
    } else {
      setHasExistingKey(false)
    }
  }, [existingConfig, reset])

  // Configure mutation
  const configureMutation = useMutation({
    mutationFn: (data: SharePointConfig) => integrationService.configureSharePoint(data),
    onSuccess: () => {
      // If there's a private key file, upload it next
      if (privateKeyFile) {
        uploadKeyMutation.mutate(privateKeyFile)
      } else {
        // No key file, we're done
        toast({
          title: t('integration.configured'),
          description: t('integration.sharepoint_success'),
        })
        onSuccess()
      }
    },
    onError: () => {
      toast({
        title: t('common.error'),
        description: t('integration.configure_error'),
        variant: 'destructive',
      })
    },
  })

  // Upload key mutation (runs after configure mutation if file present)
  const uploadKeyMutation = useMutation({
    mutationFn: (file: File) => integrationService.uploadSharePointKey(file),
    onSuccess: () => {
      toast({
        title: t('integration.configured'),
        description: t('integration.sharepoint_success'),
      })
      onSuccess()
    },
    onError: (error: unknown) => {
      console.error('Error uploading SharePoint key:', error)
      const err = error as Record<string, unknown>
      console.error('Error details:', {
        message: err.message,
        status: err.status,
        error: err.error,
        details: err.details
      })

      // Try to extract backend error message
      const details = err.details as Record<string, unknown> | undefined
      const errorMessage = (details?.error as string) || (err.error as string) || (err.message as string) || 'Unknown error'

      toast({
        title: t('common.error'),
        description: `${t('integration.key_upload_error')}: ${errorMessage}`,
        variant: 'destructive',
      })
    },
  })

  const onSubmit = (data: SharePointFormData) => {
    configureMutation.mutate(data as SharePointConfig)
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      // Validate file type
      if (!file.name.endsWith('.pem')) {
        toast({
          title: t('common.error'),
          description: 'Please select a .pem file',
          variant: 'destructive',
        })
        return
      }
      setPrivateKeyFile(file)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader className="space-y-4">
          <div className="flex items-center gap-3">
            <img src={SharePointLogo} alt="SharePoint" className="h-8 w-8" />
            <DialogTitle>{t('integration.configure_sharepoint')}</DialogTitle>
          </div>
          <DialogDescription>
            {t('integration.sharepoint_description')}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Tenant ID */}
          <div>
            <Label htmlFor="spTenantId">{t('integration.sp_tenant_id')}</Label>
            <Input
              id="spTenantId"
              {...register('spTenantId')}
              placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
            />
            {errors.spTenantId && (
              <p className="text-sm text-red-600 mt-1">{errors.spTenantId.message}</p>
            )}
          </div>

          {/* Client ID */}
          <div>
            <Label htmlFor="spClientId">{t('integration.sp_client_id')}</Label>
            <Input
              id="spClientId"
              {...register('spClientId')}
              placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
            />
            {errors.spClientId && (
              <p className="text-sm text-red-600 mt-1">{errors.spClientId.message}</p>
            )}
          </div>

          {/* Certificate Thumbprint */}
          <div>
            <Label htmlFor="spCertThumbprint">{t('integration.sp_cert_thumbprint')}</Label>
            <Input
              id="spCertThumbprint"
              {...register('spCertThumbprint')}
              placeholder="XXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX"
              type="password"
            />
            {errors.spCertThumbprint && (
              <p className="text-sm text-red-600 mt-1">{errors.spCertThumbprint.message}</p>
            )}
          </div>

          {/* Private Key File Upload */}
          <div>
            <Label htmlFor="privateKey">{t('integration.sp_private_key')}</Label>
            <div className="flex items-center space-x-2">
              <Input
                id="privateKey"
                type="file"
                accept=".pem"
                onChange={handleFileChange}
                className="flex-1"
              />
              {privateKeyFile ? (
                <div className="flex items-center space-x-1 text-sm text-green-600">
                  <FileText className="h-4 w-4" />
                  <span>{privateKeyFile.name}</span>
                </div>
              ) : hasExistingKey ? (
                <div className="flex items-center space-x-1 text-sm text-blue-600">
                  <FileText className="h-4 w-4" />
                  <span>{t('integration.key_already_uploaded')}</span>
                </div>
              ) : null}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {hasExistingKey && !privateKeyFile
                ? t('integration.sp_private_key_replace_help')
                : t('integration.sp_private_key_help')}
            </p>
          </div>

          {/* SharePoint Host */}
          <div>
            <Label htmlFor="spHost">{t('integration.sp_host')}</Label>
            <Input
              id="spHost"
              {...register('spHost')}
              placeholder="yourdomain.sharepoint.com"
            />
            {errors.spHost && (
              <p className="text-sm text-red-600 mt-1">{errors.spHost.message}</p>
            )}
          </div>

          {/* Site Path */}
          <div>
            <Label htmlFor="spSitePath">{t('integration.sp_site_path')}</Label>
            <Input
              id="spSitePath"
              {...register('spSitePath')}
              placeholder="/sites/YourSiteName"
            />
            {errors.spSitePath && (
              <p className="text-sm text-red-600 mt-1">{errors.spSitePath.message}</p>
            )}
          </div>

          {/* Optional File Paths */}
          <div className="space-y-4 pt-4 border-t">
            <h4 className="font-medium text-sm">{t('integration.sp_optional_paths')}</h4>

            <div>
              <Label htmlFor="spPiketnummersPath">{t('integration.sp_piketnummers_path')}</Label>
              <Input
                id="spPiketnummersPath"
                {...register('spPiketnummersPath')}
                placeholder="Library/Folder/piketnummers.xlsx"
              />
            </div>

            <div>
              <Label htmlFor="spHulpdienstenPath">{t('integration.sp_hulpdiensten_path')}</Label>
              <Input
                id="spHulpdienstenPath"
                {...register('spHulpdienstenPath')}
                placeholder="Library/Folder/hulpdiensten.xlsx"
              />
            </div>

            <div>
              <Label htmlFor="spBetrokkenPartijenPath">
                {t('integration.sp_betrokken_partijen_path')}
              </Label>
              <Input
                id="spBetrokkenPartijenPath"
                {...register('spBetrokkenPartijenPath')}
                placeholder="Library/Folder/betrokken_partijen.xlsx"
              />
            </div>

            <div>
              <Label htmlFor="spCrisiscommunicatiePath">
                {t('integration.sp_crisiscommunicatie_path')}
              </Label>
              <Input
                id="spCrisiscommunicatiePath"
                {...register('spCrisiscommunicatiePath')}
                placeholder="Piketinformatie/01 Contactgegevens/02 Crisiscommunicatie/Contactgegevens crisiscomm....xlsx"
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              disabled={configureMutation.isPending || uploadKeyMutation.isPending}
            >
              {(configureMutation.isPending || uploadKeyMutation.isPending)
                ? t('common.saving')
                : t('common.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
